import React, { useState, useEffect } from 'react';
import { useLeave } from '../context/LeaveContext';
import LeaveForm from '../components/leave/LeaveForm';
import '../components/leave/leave.css';

const LeaveCalendar = ({ yearMonth }) => {
     const { myLeaves, fetchMyLeaves, apply, loading } = useLeave();

     const today = new Date();
     // format YYYY-MM using local date parts to avoid timezone/ISO shift
     const toMonthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
     const currentMonthStr = toMonthStr(today);
     // allow selecting up to next month
     const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
     const nextMonthStr = toMonthStr(nextMonthDate);


     const [selectedMonth, setSelectedMonth] = useState(yearMonth || currentMonthStr);
     const userCode = sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || '';
     const yesterday = new Date();
     yesterday.setDate(today.getDate() - 1);
     yesterday.setHours(0, 0, 0, 0);

     const [openForm, setOpenForm] = useState(false);
     const [initial, setInitial] = useState({});

     // fetch leaves for the selected month (pass month as param to backend)
     useEffect(() => {
          if (!userCode) return; // wait until user_code is available
          fetchMyLeaves(userCode);
     }, [fetchMyLeaves, userCode, selectedMonth]);

     const normalizeForForm = (leave) => ({
          leave_id: leave.leave_id || leave.id || null,
          from_date: leave.from_date || leave.start_date || leave.start_date || '',
          to_date: leave.to_date || leave.end_date || leave.end_date || '',
          leave_type: leave.leave_type || leave.type || '',
          reason: leave.reason || leave.remarks || '',
          multiple_days: (leave.days && Number(leave.days) > 1) || (leave.from_date && leave.to_date && leave.from_date !== leave.to_date) || false,
          emp_code: leave.emp_code || leave.applied_by || sessionStorage.getItem('user_code') || ''
     });

     const handleDayClick = (dateStr, existingLeave = null) => {
          const clicked = new Date(dateStr + 'T00:00:00');
          if (clicked < yesterday) {
               alert('You cannot apply leave for dates earlier than yesterday.');
               return;
          }
          if (existingLeave) {
               setInitial(normalizeForForm(existingLeave));
               setOpenForm(true);
               return;
          }
          setInitial({ from_date: dateStr, to_date: dateStr });
          setOpenForm(true);
     };

     const dateInRange = (dateStr, from, to) => {
          if (!from) return false;
          const d = new Date(dateStr + 'T00:00:00').getTime();
          const a = new Date((from || dateStr) + 'T00:00:00').getTime();
          const b = new Date((to || from || dateStr) + 'T00:00:00').getTime();
          return d >= a && d <= b;
     };

     const buildCalendar = () => {
          const [year, month] = (selectedMonth || currentMonthStr).split('-').map(Number);
          const firstDay = new Date(year, month - 1, 1).getDay();
          const daysInMonth = new Date(year, month, 0).getDate();
          const cells = [];
          for (let i = 0; i < firstDay; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) {
               const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
               // find all leaves that include this date
               const leavesForDate = (myLeaves || []).filter(l => {
                    // support backend fields: from_date / to_date or start_date / end_date
                    const from = l.from_date || l.start_date || l.from;
                    const to = l.to_date || l.end_date || l.to;
                    return dateInRange(dateStr, from, to);
               });
               const isPast = new Date(dateStr + 'T00:00:00') < yesterday;
               cells.push({ day: d, dateStr, leaves: leavesForDate, isPast });
          }
          return cells;
     };

     const cells = buildCalendar();
     const currentEmpCode = userCode;
     return (
          <div className="leave-calendar-card">
               <div className="calendar-header row" style={{ alignItems: 'center', marginBottom: 8 }}>
                    <div className="col"><h3>Leave Calendar</h3></div>
                    <div className="col-sm-3">
                         <input
                              className="form-control form-control-sm month-picker"
                              type="month"
                              value={selectedMonth}
                              onChange={e => setSelectedMonth(e.target.value)}
                              max={nextMonthStr}
                         />
                    </div>
                    <div className="col-sm-3 text-end">
                         <button className="btn btn-secondary btn-sm" onClick={() => { window.location.href = '/leaves/my'; }}>
                              View My Leaves List
                         </button>
                    </div>
               </div>

               {loading && <div style={{ padding: 12 }}>Loading leaves...</div>}

               <div className="calendar-grid small" aria-hidden={loading}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(h => <div key={h} className="weekday">{h}</div>)}
                    {cells.map((c, idx) => {
                         if (!c) return <div key={idx} className="calendar-cell empty" />;
                         const hasLeave = c.leaves && c.leaves.length > 0;
                         const primary = hasLeave ? c.leaves[0] : null;
                         return (
                              <div
                                   key={c.dateStr}
                                   className={`calendar-cell ${hasLeave ? 'has-leave' : ''} ${c.isPast ? 'is-past' : ''}`}
                                   onClick={() => {
                                        if (hasLeave) handleDayClick(c.dateStr, c.leaves[0]);
                                        else handleDayClick(c.dateStr);
                                   }}
                                   title={c.isPast ? 'Cannot apply for this past date' : (hasLeave ? `${primary.leave_type || primary.type || 'Leave'} (${primary.status || primary.leave_status || 'Applied'})` : 'Click to apply leave')}
                                   style={c.isPast ? { cursor: 'not-allowed', opacity: 0.65 } : { cursor: 'pointer' }}
                              >
                                   <div className="cell-day">{c.day}</div>

                                   {hasLeave && (
                                        <div style={{ marginTop: 28 }}>
                                             <div className="cell-leave-type" style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                                                  {primary.leave_type || primary.type || 'Leave'}
                                             </div>
                                             <div className="cell-times" style={{ marginTop: 6 }}>
                                                  <div className="worked-hours" style={{ fontSize: 12, color: '#475569' }}>
                                                       {primary.status || primary.leave_status || ''}
                                                  </div>
                                             </div>

                                             {c.leaves.length > 1 && (
                                                  <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                                                       +{c.leaves.length - 1} more
                                                  </div>
                                             )}
                                        </div>
                                   )}
                              </div>
                         );
                    })}
               </div>

               <LeaveForm
                    open={openForm}
                    initial={initial}
                    emp_code={currentEmpCode}
                    onClose={() => setOpenForm(false)}
                    onSubmit={async (formPayload) => {
                         try {
                              const res = await apply(formPayload); // sends payload to /leave/apply
                              if (res && res.status) {
                                   await fetchMyLeaves(currentEmpCode); // refresh for this user
                              }
                              return res;
                         } catch (err) {
                              // rethrow so LeaveForm can handle/display error
                              throw err;
                         }
                    }}
               />
          </div>
     );
};

export default LeaveCalendar;